"use client";
import { jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useMemo, useState } from "react";
import { useSearchFormAutocompleteConfig } from "../../context/SearchFormAutocompleteContext.js";
import { useCurrentLocation } from "../../hooks/useCurrentLocation.js";
import { useTypeAheadFeedback } from "../../hooks/useTypeAheadFeedback.js";
import { useTypeAheadSearch } from "../../hooks/useTypeAheadSearch.js";
import { AutocompleteSearchField } from "./AutocompleteSearchField.js";
const EMPTY_LOCATIONS = [];
const ConnectedAutocompleteSearchField_ConnectedAutocompleteSearchField = /*#__PURE__*/ forwardRef(function({ typeAheadEndpoint = 'hotels', debounceTime, airportNestingEnabled = false, neighbourhoodNestingEnabled = false, hasAISearch = false, hasLTRSearch = false, isMobile = false, onFilterChipToggle, ...rest }, ref) {
    const config = useSearchFormAutocompleteConfig();
    const [searchValue, setSearchValue] = useState(null);
    const searchKey = rest.searchKey;
    const persistedRecents = useMemo(()=>config.recentSearches?.getPersisted?.({
            searchKey,
            typeAheadEndpoint
        }) ?? EMPTY_LOCATIONS, [
        config.recentSearches,
        searchKey,
        typeAheadEndpoint
    ]);
    const isHotelEndpoint = 'hotels' === typeAheadEndpoint || 'hotelsV2' === typeAheadEndpoint;
    const recommendedItems = isHotelEndpoint ? config.recommendedSearches?.items ?? EMPTY_LOCATIONS : EMPTY_LOCATIONS;
    const typeAheadResult = useTypeAheadSearch({
        searchValue,
        typeAheadEndpoint,
        config: config.typeahead,
        debounceTime,
        airportNestingEnabled,
        neighbourhoodNestingEnabled,
        hasAISearch,
        hasLTRSearch,
        isMobile,
        onLtrSearch: config.analytics?.onLtrSearch,
        onResultCity: config.analytics?.onResultCity
    });
    const geolocation = useCurrentLocation({
        nearestCityBaseUrl: config.geolocation?.nearestCityBaseUrl,
        onError: config.analytics?.onError
    });
    const sendFeedback = useTypeAheadFeedback(config.typeahead);
    const onTypeaheadFeedback = useCallback((payload)=>{
        sendFeedback({
            ...payload,
            typeAheadEndpoint: payload.typeAheadEndpoint
        });
    }, [
        sendFeedback
    ]);
    const adapter = useMemo(()=>({
            session: {
                clientGUID: config.session?.clientGUID ?? '',
                appName: config.session?.appName ?? '',
                appVersion: config.session?.appVersion ?? '',
                signedIn: config.session?.signedIn ?? false
            },
            recentSearches: {
                local: persistedRecents,
                fetched: config.recentSearches?.fetched ?? EMPTY_LOCATIONS,
                isFetchedReady: config.recentSearches?.isFetchedReady ?? true,
                abandoned: config.recentSearches?.abandoned ?? EMPTY_LOCATIONS,
                isAbandonedReady: config.recentSearches?.isAbandonedReady ?? true,
                viewed: config.recentSearches?.viewed ?? EMPTY_LOCATIONS,
                isViewedReady: config.recentSearches?.isViewedReady ?? true
            },
            recommendedSearches: {
                items: recommendedItems
            },
            geolocation,
            typeAheadResult: {
                items: typeAheadResult.items,
                duration: typeAheadResult.duration,
                source: typeAheadResult.source
            },
            onTypeaheadFeedback,
            onTypeaheadClick: config.analytics?.onTypeaheadClick,
            onAutocompleteOpen: config.analytics?.onAutocompleteOpen,
            onFilterChipToggle: onFilterChipToggle ?? config.analytics?.onFilterChipToggle,
            onAiSearchClick: config.analytics?.onAiSearchClick,
            resolveItemFirstLineSuffix: config.resolveItemFirstLineSuffix ? (item)=>config.resolveItemFirstLineSuffix?.(item, {
                    typeAheadEndpoint
                }) : void 0,
            resolveFilterChipData: config.resolveFilterChipData
        }), [
        config,
        geolocation,
        typeAheadResult.items,
        typeAheadResult.duration,
        typeAheadResult.source,
        onTypeaheadFeedback,
        onFilterChipToggle,
        persistedRecents,
        recommendedItems,
        typeAheadEndpoint
    ]);
    return /*#__PURE__*/ jsx(AutocompleteSearchField, {
        ...rest,
        ref: ref,
        typeAheadEndpoint: typeAheadEndpoint,
        searchValue: searchValue,
        onSearchValueChange: setSearchValue,
        airportNestingEnabled: airportNestingEnabled,
        neighbourhoodNestingEnabled: neighbourhoodNestingEnabled,
        isMobile: isMobile,
        hasAISearch: hasAISearch,
        hasLTRSearch: hasLTRSearch,
        adapter: adapter
    });
});
export { ConnectedAutocompleteSearchField_ConnectedAutocompleteSearchField as ConnectedAutocompleteSearchField };
